import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Chip } from "ui/chip";
import ContentTypeMismatchIcon from "components/partials/icon/content-type-mismatch-icon";
import { Checkbox } from "ui/checkbox";
import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import CreativeTableActionsMenu from "containers/admin/clients/campaign/components/creative-table-actions-menu.tsx/creative-table-actions-menu";

import { findStatusDetails } from "utilities/touchpoint";
import { getBatchPath, getTouchpointPaths } from "utilities/app-routes";
import { getSyncBadgeConfig } from "utilities/sync-status";
import {
  formatFiscalYear,
  getDisplayTouchpointType,
  getDisplayTouchpointIcon,
  toSpacedTitleCase,
} from "utilities";
import { getTouchpointClassChipColor } from "utilities/touchpoint-class-chip";
import { formatDateTime } from "utilities/date";

import { TouchpointType, TouchpointTypeLabel } from "types/touchpoint";
import { TouchpointStatusDictionaryItem } from "hooks/api/dictionary";
import { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { TouchpointDTOBatchJobType } from "generated/schemas";
import { useCreativeTableSelection } from "containers/admin/clients/campaign/campaign-creative-page/components/creactive-table/creative-table-selection-context";

import Icons from "assets";
import { Button } from "ui/button";

const MONDAY_LINK_SX: SxProps<Theme> = { minWidth: 158, ml: 0.5 };

interface CreateColumnsProps {
  clientId: string;
  campaignId: string;
  touchpointVersionStatuses: TouchpointStatusDictionaryItem[];
  isClientUser: boolean;
  isBatchManagementEnabled?: boolean;
  folderId?: string;
}

const headerStyles = "text-gray-600 font-bold leading-5 pl-4 whitespace-nowrap";

const SelectAllCreativesCheckbox = () => {
  const { selectedIds, setSelectedIds, displayedTouchpoints, isCurrentPageAllSelected } =
    useCreativeTableSelection();

  const handleChange = () => {
    if (isCurrentPageAllSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !displayedTouchpoints.some((tp) => tp.id === id)),
      );
    } else {
      setSelectedIds((prev) => {
        const newIds = [...prev];
        displayedTouchpoints.forEach((tp) => {
          if (!newIds.includes(tp.id)) newIds.push(tp.id);
        });
        return newIds;
      });
    }
  };

  return (
    <div className="flex justify-start pl-4">
      <Checkbox
        size="sm"
        aria-label="Select all creatives"
        checked={isCurrentPageAllSelected}
        indeterminate={
          !isCurrentPageAllSelected &&
          displayedTouchpoints.some((tp) => selectedIds.includes(tp.id))
        }
        onChange={handleChange}
      />
    </div>
  );
};

const SelectCreativeCheckbox = ({ touchpointId }: { touchpointId: string }) => {
  const { selectedIds, setSelectedIds } = useCreativeTableSelection();
  const isSelected = selectedIds.includes(touchpointId);

  const handleChange = () => {
    setSelectedIds((prev) =>
      isSelected ? prev.filter((id) => id !== touchpointId) : [...prev, touchpointId],
    );
  };

  return (
    <div className="flex justify-start pl-4">
      <Checkbox
        size="sm"
        aria-label="Select creative"
        checked={isSelected}
        onChange={handleChange}
      />
    </div>
  );
};

export const createCreactiveTableColumns = <T extends TouchpointAttributes>({
  clientId,
  campaignId,
  touchpointVersionStatuses,
  isClientUser,
  isBatchManagementEnabled = false,
  folderId,
}: CreateColumnsProps): ColumnDef<T>[] => {
  const columns: ColumnDef<T>[] = [
    {
      id: "checkbox",
      enableSorting: false,
      header: () => <SelectAllCreativesCheckbox />,
      cell: ({ row }) => <SelectCreativeCheckbox touchpointId={row.original.id} />,
      size: 40,
      meta: {
        className: "px-0",
        headerClassName: "px-0",
        isFixedWidth: true,
      },
    },
    {
      id: "warningIcon",
      enableSorting: false,
      cell: ({ row }) => {
        return (
          !row.original.validContentType && (
            <div className="flex justify-center w-full">
              <div className="size-5">
                <ContentTypeMismatchIcon customClass="w-full" />
              </div>
            </div>
          )
        );
      },
      size: 40,
      meta: {
        className: "px-0",
        isFixedWidth: true,
      },
    },
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      enableSorting: true,
      cell: ({ row }) => {
        const type = row.original.type as TouchpointType;
        const label =
          type === TouchpointType.OTHER
            ? TouchpointTypeLabel.OTHER
            : getDisplayTouchpointType(type);
        const icon = getDisplayTouchpointIcon(type);

        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            {icon}
            {label}
          </div>
        );
      },
      size: 140,
      minSize: 140,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "name",
      header: "Creative name",
      accessorKey: "name",
      enableSorting: true,
      cell: ({ row }) => {
        const { touchpointPreviewPath } = getTouchpointPaths({
          clientId,
          campaignId,
          touchpointId: row.original.id,
        });
        const syncStatus = row.original.batchJobExecutionStatus;
        const syncBadge = getSyncBadgeConfig(syncStatus, row.original.batchJobType);
        const mondayBoardUrl = row.original.mondayBoardLink;
        const SyncBadgeIcon =
          syncBadge?.jobType === TouchpointDTOBatchJobType.WRITING
            ? Icons.CampaignWriter
            : Icons.MondayBoard;

        return (
          <>
            <Link
              to={touchpointPreviewPath}
              className="text-aqua-600 hover:text-aqua-700 font-semibold text-base"
              aria-disabled={false}
              tabIndex={undefined}>
              {row.original.name}{" "}
              {!isClientUser && syncBadge && (
                <Chip
                  variant="outlined"
                  color={syncBadge.color}
                  size="small"
                  label={syncBadge.label}
                  icon={<SyncBadgeIcon />}
                />
              )}
            </Link>
            {!isClientUser && !!mondayBoardUrl && (
              <Button
                variant="text"
                size="small"
                sx={MONDAY_LINK_SX}
                endIcon={<Icons.Forward className="" />}
                onClick={() => window.open(mondayBoardUrl, "_blank", "noopener,noreferrer")}>
                View on monday.com
              </Button>
            )}
          </>
        );
      },
      size: 220,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "fiscalYear",
      header: "Fiscal year",
      accessorKey: "fiscalYear",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-gray-600">
          {formatFiscalYear(row.original.latestVersion?.fiscalYear)}
        </div>
      ),
      size: 110,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "copyCode",
      header: "Copy code",
      accessorKey: "copyCode",
      enableSorting: true,
      cell: ({ row }) => <div className="text-gray-600">{row.original.copyCode}</div>,
      size: 130,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "latestVersion",
      header: "Newest version status",
      enableSorting: false,
      cell: ({ row }) => {
        const latestVersionStatus = row.original.latestVersion?.status;
        const status = findStatusDetails(latestVersionStatus, touchpointVersionStatuses);

        return status ? <StatusIndicator versionStatuses={status} /> : null;
      },
      size: 350,
      meta: {
        headerClassName: headerStyles,
      },
    },
    ...(isBatchManagementEnabled && !isClientUser
      ? [
          {
            id: "batch",
            header: "Batch",
            enableSorting: false,
            cell: ({ row }) => {
              const batchId = row.original.touchpointBatchId;
              const batchName = row.original.touchpointBatchName;

              if (!batchId || !batchName) {
                return null;
              }

              const batchPath = getBatchPath({ clientId, batchId, isClientUser });

              return (
                <Link
                  to={batchPath}
                  className="flex min-w-0 items-center gap-1 text-aqua-600 hover:text-aqua-700"
                  aria-disabled={false}
                  tabIndex={undefined}>
                  <Icons.Layers className="size-4 shrink-0" aria-hidden />
                  <span className="line-clamp-1 font-semibold">{batchName}</span>
                </Link>
              );
            },
            size: 180,
            meta: {
              className: "pl-8",
              headerClassName: twMerge(headerStyles, "pl-8"),
            },
          } satisfies ColumnDef<T>,
        ]
      : []),
    {
      id: "populationType",
      header: "Population type",
      accessorKey: "populationType",
      enableSorting: true,
      cell: ({ row }) =>
        row.original.populationType ? (
          <Chip
            size="small"
            variant="outlined"
            label={toSpacedTitleCase(row.original.populationType)}
          />
        ) : null,
      size: 180,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "classes",
      header: "Class",
      accessorKey: "classes",
      enableSorting: false,
      cell: ({ row }) => {
        if (!row.original.classes?.length) {
          return null;
        }

        return (
          <div className="flex flex-wrap gap-1">
            {row.original.classes.map((classType) => (
              <Chip
                key={classType}
                size="small"
                variant="outlined"
                color={getTouchpointClassChipColor(classType)}
                label={toSpacedTitleCase(classType)}
              />
            ))}
          </div>
        );
      },
      size: 100,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "publishState",
      header: "Publish state",
      enableSorting: false,
      cell: ({ row }) =>
        !!row.original.publishedVersion && (
          <div className="flex items-center gap-2">
            <Chip variant="filled" color="primary" size="small" label="Published" />
            <span className="text-gray-500">v{row.original.publishedVersion}</span>
          </div>
        ),
      size: 130,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "lastModifiedDate",
      header: "Last updated",
      accessorKey: "lastModifiedDate",
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
      ),
      size: 170,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => <CreativeTableActionsMenu touchpoint={row.original} />,
      size: 86,
      meta: {
        className: "pr-8",
        isHeaderCentered: true,
        headerClassName: headerStyles,
        isPinnedRight: true,
      },
    },
  ];

  if (!folderId) {
    columns.unshift({
      id: "folderId",
      accessorKey: "folderId" as keyof T,
      enableSorting: false,
      meta: {
        headerClassName: headerStyles,
      },
    });
  }

  return columns;
};
